import { Box, Button, Typography } from '@mui/material';
import EntitlementsWrapper, { greaterThan } from 'app/common/components/entitlements-wrapper';
import Table, { HeaderCell } from 'app/common/components/table';
import { SchemaForm } from 'app/schema-form';
import { countries, languages } from 'countries-list';
import { fromJS } from 'immutable';
import type React from 'react';
import type { TableData } from '../../../common/components/table/types';
import Autocomplete from '../../../common/components/widgets/autocomplete';
import { FORM_NAME } from '../../constants';
import CountryCell from './table/country-cell';
import DefaultCell from './table/default-cell';
import LanguageCell from './table/language-cell';
import RemoveCell from './table/remove-cell';

function LocaleWrapper({ children }: { readonly children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        width: '100%'
      }}
    >
      {children}
    </Box>
  );
}

const languageSuggestions = Object.entries(languages).map(([code, language]) => ({
  value: code,
  label: `${language.name} - ${code}`
}));

const countrySuggestions = Object.entries(countries).map(([code, country]) => {
  const codeLowerCase = code.toLowerCase();
  return {
    value: codeLowerCase,
    label: `${country.emoji} ${country.name} - ${codeLowerCase}`
  };
});

const columns = [
  {
    name: 'language',
    title: 'Language',
    component: LanguageCell
  },
  {
    name: 'country',
    title: 'Country',
    component: CountryCell
  },
  {
    name: 'code',
    title: 'Code'
  },
  {
    name: 'default',
    title: 'Default',
    headerComponent: HeaderCell,
    component: DefaultCell
  },
  {
    name: 'remove',
    title: 'Remove',
    headerComponent: HeaderCell,
    component: RemoveCell
  }
];

type LocalesData = {
  language: string;
  country: string;
  default: boolean;
};

const formSchema = fromJS({
  type: 'object',
  properties: {
    language: {
      type: 'string',
      title: 'Language'
    },
    country: {
      type: 'string',
      title: 'Country'
    },
    default: {
      type: 'boolean'
    }
  },
  required: ['language']
});

const formConfig = fromJS({
  wrapper: 'localeWrapper',
  order: ['language', 'country'],
  properties: {
    language: {
      widget: 'autocomplete',
      options: languageSuggestions,
      sx: { mb: 0 }
    },
    country: {
      widget: 'autocomplete',
      options: countrySuggestions,
      sx: { mb: 0 }
    }
  }
});

const widgets = {
  autocomplete: Autocomplete,
  localeWrapper: LocaleWrapper
};

export type LocalesHookProps = {
  readonly locales: TableData[];
  readonly isSaving: boolean;
  readonly defaultLocaleInitialData: LocalesData;
  readonly localesEntitlement: number;
};

export type LocalesProps = LocalesHookProps;

export const Locales: React.FC<LocalesProps> = (props) => {
  const { locales, isSaving, localesEntitlement, defaultLocaleInitialData } = props;

  const multipleLocales = localesEntitlement > 1;
  return (
    <>
      {!multipleLocales && (
        <Typography variant="h3" gutterBottom>
          Default Locale
        </Typography>
      )}
      <Box
        sx={{
          border: multipleLocales ? undefined : '1px solid #eceff1',
          padding: 2,
          form: {
            display: 'flex',
            alignItems: 'flex-start'
          }
        }}
      >
        <SchemaForm
          formName={FORM_NAME}
          schema={formSchema}
          config={formConfig}
          widgets={widgets}
          initialData={multipleLocales ? undefined : defaultLocaleInitialData}
        >
          <Button disabled={isSaving} type="submit" variant="contained" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
            {multipleLocales ? 'Add Locale' : 'Save'}
          </Button>
        </SchemaForm>
      </Box>
      <EntitlementsWrapper entitlement="locales" predicate={greaterThan(1)}>
        <Table tableName="locales-table" data={locales} columns={columns} />
      </EntitlementsWrapper>
    </>
  );
};
